import React from 'react';
import { motion } from 'framer-motion';
import { Instagram, Facebook, Twitter, Clock, Phone, Mail, MapPin, Code2 } from 'lucide-react';
import { restaurantInfo } from '../data/mock';

const Footer = () => {
  return (
    <footer className="bg-black text-white py-20 px-6 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid md:grid-cols-5 gap-12 mb-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-4xl font-black mb-4 bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
              {restaurantInfo.name}
            </h3>
            <p className="text-gray-400 leading-relaxed">
              {restaurantInfo.description}
            </p>
          </motion.div>

          {/* Horarios */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-400" />
              Horarios
            </h4>
            <div className="space-y-3 text-gray-400">
              <p>
                <span className="font-semibold text-white">Lun - Vie:</span><br />
                {restaurantInfo.hours.weekdays}
              </p>
              <p>
                <span className="font-semibold text-white">Sáb - Dom:</span><br />
                {restaurantInfo.hours.weekends}
              </p>
            </div>
          </motion.div>

          {/* Contacto */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-xl font-bold mb-6">Contacto</h4>
            <div className="space-y-4">
              <a
                href={`tel:${restaurantInfo.contact.phone}`}
                className="flex items-center gap-3 text-gray-400 hover:text-orange-400 transition-colors duration-300 group"
              >
                <Phone className="w-5 h-5" />
                <span className="group-hover:translate-x-1 transition-transform duration-300">
                  {restaurantInfo.contact.phone}
                </span>
              </a>
              <a
                href={`mailto:${restaurantInfo.contact.email}`}
                className="flex items-center gap-3 text-gray-400 hover:text-orange-400 transition-colors duration-300 group"
              >
                <Mail className="w-5 h-5" />
                <span className="group-hover:translate-x-1 transition-transform duration-300">
                  {restaurantInfo.contact.email}
                </span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                <span>{restaurantInfo.contact.address}</span>
              </div>
            </div>
          </motion.div>

          {/* Social */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-xl font-bold mb-6">Síguenos</h4>
            <div className="space-y-4">
              <a
                href="#"
                className="flex items-center gap-3 text-gray-400 hover:text-orange-400 transition-colors duration-300 group"
              >
                <Instagram className="w-5 h-5" />
                <span className="group-hover:translate-x-1 transition-transform duration-300">
                  {restaurantInfo.social.instagram}
                </span>
              </a>
              <a
                href="#"
                className="flex items-center gap-3 text-gray-400 hover:text-orange-400 transition-colors duration-300 group"
              >
                <Facebook className="w-5 h-5" />
                <span className="group-hover:translate-x-1 transition-transform duration-300">
                  {restaurantInfo.social.facebook}
                </span>
              </a>
              <a
                href="#"
                className="flex items-center gap-3 text-gray-400 hover:text-orange-400 transition-colors duration-300 group"
              >
                <Twitter className="w-5 h-5" />
                <span className="group-hover:translate-x-1 transition-transform duration-300">
                  {restaurantInfo.social.twitter}
                </span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Bottom bar with creative developer credit */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="pt-8 border-t border-zinc-800/80 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs sm:text-sm text-gray-500"
        >
          <p>
            &copy; {new Date().getFullYear()} {restaurantInfo.name}. Todos los derechos reservados.
          </p>

          <a
            href="https://jpfranco.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 group bg-zinc-900/90 hover:bg-zinc-800/90 border border-zinc-800/80 hover:border-orange-500/40 px-3.5 py-1.5 rounded-full transition-all duration-300 shadow-md"
          >
            <span className="text-gray-400 group-hover:text-gray-300 transition-colors">Desarrollado por</span>
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
            <span className="font-extrabold bg-gradient-to-r from-orange-400 via-amber-300 to-orange-500 bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300 inline-block tracking-wide">
              JpFranco
            </span>
            <Code2 className="w-3.5 h-3.5 text-orange-400 group-hover:rotate-12 transition-transform duration-300 ml-0.5" />
          </a>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;